import { c as _c } from "react/compiler-runtime";
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { css, type Styles } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectExploreActionMenuPinId } from '@repo/store/maps/slices/clientContext';
import { selectIsEditingCustomPin, selectIsEditingNewlyCreatedPin, selectIsInCreatorMode, selectPreviewMode, setPreviewMode } from '@repo/store/maps/slices/mapData';
import { DEFAULT_PIN_COLOR } from '@repo/store/maps/utils/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { LocationInfoDebug } from '../creator-tools/components/location-info-debug';
import { useAiStreamAuth } from '../creator-tools/hooks/use-ai-stream-auth';
import { useMapControls } from '../game-map/hooks/useMapControls';
import { useMapPinOperations } from '../game-map/hooks/useMapPinOperations';
import { HeroActionDrawer } from './hero-action-drawer';
import { useLocationEditingState } from './hooks/useLocationEditingState';
import { useLocationInfo } from './hooks/useLocationInfo';
import { LabelInfoEditing } from './label-info-editing';
import { LocationInfoDisplay } from './location-info-display';
import { LocationInfoEditing } from './location-info-editing';
import { Object3dInfoEditing } from './object-3d-info-editing';
import { RegionInfoEditing } from './region-info-editing';
const MAX_HEIGHT = 'min(500px, calc(100vh - 56px - 16px))';
const LocationInfoContainer = t0 => {
  const $ = _c(17);
  const {
    children,
    locationStyleOverride,
    edgeToEdgeContent: t1,
    isMobilePane: t2,
    flushContent: t3
  } = t0;
  const edgeToEdgeContent = t1 === undefined ? false : t1;
  const isMobilePane = t2 === undefined ? false : t2;
  const flushContent = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== edgeToEdgeContent || $[1] !== flushContent || $[2] !== isMobilePane || $[3] !== locationStyleOverride) {
    t4 = css.raw({
      width: "[340px]",
      background: "[linear-gradient(rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.075)), linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9))]",
      borderRadius: "32",
      overflow: "hidden",
      boxShadow: "lg",
      zIndex: "[2]",
      backdropFilter: isMobilePane ? "[none]" : "[blur(16px)]",
      padding: edgeToEdgeContent ? "none" : "16",
      paddingBottom: flushContent ? "none" : "16",
      _compact: {
        maxWidth: "[260px]",
        borderRadius: "24",
        paddingBottom: flushContent ? "none" : "12"
      },
      smDown: {
        width: "full",
        maxWidth: "[calc(100vw - 32px)]",
        marginX: "12"
      }
    }, locationStyleOverride);
    $[0] = edgeToEdgeContent;
    $[1] = flushContent;
    $[2] = isMobilePane;
    $[3] = locationStyleOverride;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = isMobilePane ? "12" : "8";
  let t6;
  if ($[5] !== isMobilePane) {
    t6 = !isMobilePane && {
      maxHeight: MAX_HEIGHT
    };
    $[5] = isMobilePane;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== isMobilePane) {
    t7 = isMobilePane && {
      flex: 1,
      minHeight: 0
    };
    $[7] = isMobilePane;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = {
      position: "relative",
      zIndex: 4,
      ...t6,
      ...t7
    };
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== children || $[13] !== t4 || $[14] !== t5 || $[15] !== t8) {
    t9 = <FlexColumn className={t4} gap={t5} width="full" align="stretch" style={t8}>{children}</FlexColumn>;
    $[12] = children;
    $[13] = t4;
    $[14] = t5;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
};
export const LocationInfo = t0 => {
  const $ = _c(90);
  const {
    locationStyleOverride,
    isMobilePane: t1,
    hideActions: t2,
    nearbyPins
  } = t0;
  const isMobilePane = t1 === undefined ? false : t1;
  const hideActions = t2 === undefined ? false : t2;
  const {
    selectedLocation,
    selectedLabel,
    selectedRegion,
    selected3dObject,
    subcategory,
    category
  } = useLocationInfo();
  const dispatch = useAppDispatch();
  const previewMode = useAppSelector(selectPreviewMode);
  const {
    selectMarkerInMap,
    clearSelectedMarkerInMap
  } = useMapPinOperations();
  const {
    flyTo
  } = useMapControls();
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const isEditingCustomPin = useAppSelector(selectIsEditingCustomPin);
  const isEditingNewlyCreatedPin = useAppSelector(selectIsEditingNewlyCreatedPin);
  const exploreActionMenuPinId = useAppSelector(selectExploreActionMenuPinId);
  const editingLogic = useLocationEditingState(selectedLocation?.properties || null);
  const {
    authToken,
    refreshAuthToken,
    notifyUnauthorized
  } = useAiStreamAuth(isInCreatorMode);
  let t3;
  if ($[0] !== selectMarkerInMap) {
    t3 = id => {
      selectMarkerInMap(id, {
        selectSource: "drawer_nearby"
      });
    };
    $[0] = selectMarkerInMap;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handlePinSelect = t3;
  let t4;
  if ($[2] !== flyTo || $[3] !== selectedLocation) {
    t4 = () => {
      if (selectedLocation && selectedLocation.geometry.type === "Point") {
        const [lng, lat] = selectedLocation.geometry.coordinates;
        flyTo({
          center: [lng, lat],
          zoom: Math.max(12, 14),
          duration: 1500
        });
      }
    };
    $[2] = flyTo;
    $[3] = selectedLocation;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handlePanToLocation = t4;
  if (!selectedLocation && !selectedLabel && !selectedRegion && !selected3dObject && !(isEditingCustomPin && isEditingNewlyCreatedPin)) {
    return null;
  }
  const isEditingNewPinWithoutLocation = isEditingCustomPin && isEditingNewlyCreatedPin && !selectedLocation;
  const isExploreActionMenu = !!selectedLocation && !isMobilePane && exploreActionMenuPinId === selectedLocation.properties.externalId;
  let t5;
  if ($[5] !== selectedLocation?.properties) {
    t5 = selectedLocation?.properties || {};
    $[5] = selectedLocation?.properties;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    title,
    description,
    imageId,
    imagePath,
    videoPath,
    gameCoords,
    isCustomPin
  } = t5;
  const categoryColor = isCustomPin ? selectedLocation?.properties.color || DEFAULT_PIN_COLOR : category?.color || DEFAULT_PIN_COLOR;
  const subcategoryIcon = subcategory?.icon || "map-pin";
  let t6;
  if ($[7] !== categoryColor || $[8] !== handlePanToLocation || $[9] !== imageId || $[10] !== imagePath || $[11] !== subcategoryIcon || $[12] !== videoPath) {
    t6 = {
      imageId,
      imagePath,
      videoPath,
      categoryColor,
      subcategoryIcon,
      onPanToLocation: handlePanToLocation
    };
    $[7] = categoryColor;
    $[8] = handlePanToLocation;
    $[9] = imageId;
    $[10] = imagePath;
    $[11] = subcategoryIcon;
    $[12] = videoPath;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const sharedProps = t6;
  let t7;
  if ($[14] !== clearSelectedMarkerInMap) {
    t7 = () => {
      clearSelectedMarkerInMap();
    };
    $[14] = clearSelectedMarkerInMap;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleClose = t7;
  if (selectedLabel && isInCreatorMode) {
    let t8;
    if ($[16] !== selectedLabel) {
      t8 = <LabelInfoEditing label={selectedLabel} />;
      $[16] = selectedLabel;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== locationStyleOverride || $[19] !== t8) {
      t9 = <LocationInfoContainer locationStyleOverride={locationStyleOverride}>{t8}</LocationInfoContainer>;
      $[18] = locationStyleOverride;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    return t9;
  }
  if (selectedRegion && isInCreatorMode) {
    let t8;
    if ($[21] !== selectedRegion) {
      t8 = <RegionInfoEditing region={selectedRegion} />;
      $[21] = selectedRegion;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== locationStyleOverride || $[24] !== t8) {
      t9 = <LocationInfoContainer locationStyleOverride={locationStyleOverride}>{t8}</LocationInfoContainer>;
      $[23] = locationStyleOverride;
      $[24] = t8;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    return t9;
  }
  if (selected3dObject && isInCreatorMode) {
    let t8;
    if ($[26] !== selected3dObject) {
      t8 = <Object3dInfoEditing model={selected3dObject} />;
      $[26] = selected3dObject;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    let t9;
    if ($[28] !== locationStyleOverride || $[29] !== t8) {
      t9 = <LocationInfoContainer locationStyleOverride={locationStyleOverride}>{t8}</LocationInfoContainer>;
      $[28] = locationStyleOverride;
      $[29] = t8;
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    return t9;
  }
  if (isInCreatorMode && selectedLocation) {
    if (previewMode) {
      const t8 = gameCoords ?? undefined;
      const t9 = subcategory?.title;
      let t10;
      if ($[31] !== clearSelectedMarkerInMap || $[32] !== dispatch) {
        t10 = () => {
          dispatch(setPreviewMode(false));
          clearSelectedMarkerInMap();
        };
        $[31] = clearSelectedMarkerInMap;
        $[32] = dispatch;
        $[33] = t10;
      } else {
        t10 = $[33];
      }
      let t11;
      if ($[34] !== description || $[35] !== isMobilePane || $[36] !== sharedProps || $[37] !== t10 || $[38] !== t8 || $[39] !== t9 || $[40] !== title) {
        t11 = <LocationInfoDisplay {...sharedProps} title={title} description={description} gameCoords={t8} subcategoryTitle={t9} isMobilePane={isMobilePane} onClose={t10} />;
        $[34] = description;
        $[35] = isMobilePane;
        $[36] = sharedProps;
        $[37] = t10;
        $[38] = t8;
        $[39] = t9;
        $[40] = title;
        $[41] = t11;
      } else {
        t11 = $[41];
      }
      let t12;
      if ($[42] !== locationStyleOverride || $[43] !== t11) {
        t12 = <LocationInfoContainer locationStyleOverride={locationStyleOverride} edgeToEdgeContent={true}>{t11}</LocationInfoContainer>;
        $[42] = locationStyleOverride;
        $[43] = t11;
        $[44] = t12;
      } else {
        t12 = $[44];
      }
      return t12;
    }
    let t8;
    if ($[45] !== authToken || $[46] !== category || $[47] !== handleClose || $[48] !== notifyUnauthorized || $[49] !== refreshAuthToken || $[50] !== selectedLocation || $[51] !== subcategory) {
      t8 = <LocationInfoDebug key={selectedLocation.properties.externalId} selectedLocation={selectedLocation} category={category} subcategory={subcategory} authToken={authToken} refreshAuthToken={refreshAuthToken} notifyUnauthorized={notifyUnauthorized} onClose={handleClose} />;
      $[45] = authToken;
      $[46] = category;
      $[47] = handleClose;
      $[48] = notifyUnauthorized;
      $[49] = refreshAuthToken;
      $[50] = selectedLocation;
      $[51] = subcategory;
      $[52] = t8;
    } else {
      t8 = $[52];
    }
    return t8;
  }
  let t8;
  if ($[53] !== isMobilePane) {
    t8 = css.raw({
      position: "relative",
      ...(isMobilePane && {
        flex: "1",
        minHeight: "[0]",
        overflow: "hidden"
      })
    });
    $[53] = isMobilePane;
    $[54] = t8;
  } else {
    t8 = $[54];
  }
  const t9 = isMobilePane ? undefined : "full";
  let t10;
  if ($[55] !== handlePinSelect || $[56] !== isMobilePane || $[57] !== nearbyPins || $[58] !== selectedLocation) {
    t10 = selectedLocation && !isMobilePane && <HeroActionDrawer selectedPinId={selectedLocation.properties.externalId} nearbyPins={nearbyPins} onPinSelect={handlePinSelect} />;
    $[55] = handlePinSelect;
    $[56] = isMobilePane;
    $[57] = nearbyPins;
    $[58] = selectedLocation;
    $[59] = t10;
  } else {
    t10 = $[59];
  }
  const t11 = !editingLogic.isCurrentlyEditing && !isEditingNewPinWithoutLocation;
  let t12;
  if ($[60] !== clearSelectedMarkerInMap || $[61] !== description || $[62] !== editingLogic.editingState || $[63] !== editingLogic.handleCancel || $[64] !== editingLogic.handleSave || $[65] !== editingLogic.isCurrentlyEditing || $[66] !== editingLogic.isEditingNewlyCreatedPin || $[67] !== editingLogic.isSaveDisabled || $[68] !== editingLogic.updateColor || $[69] !== editingLogic.updateDescription || $[70] !== editingLogic.updateTitle || $[71] !== gameCoords || $[72] !== hideActions || $[73] !== isEditingNewPinWithoutLocation || $[74] !== isMobilePane || $[75] !== sharedProps || $[76] !== subcategory?.title || $[77] !== title) {
    t12 = editingLogic.isCurrentlyEditing || isEditingNewPinWithoutLocation ? <LocationInfoEditing {...sharedProps} title={editingLogic.editingState.title} description={editingLogic.editingState.description} color={editingLogic.editingState.color} onTitleChange={editingLogic.updateTitle} onDescriptionChange={editingLogic.updateDescription} onColorChange={editingLogic.updateColor} onSave={editingLogic.handleSave} onCancel={editingLogic.handleCancel} isSaveDisabled={editingLogic.isSaveDisabled} isEditingNewlyCreatedPin={editingLogic.isEditingNewlyCreatedPin} /> : <LocationInfoDisplay {...sharedProps} title={title} description={description} gameCoords={gameCoords ?? undefined} subcategoryTitle={subcategory?.title} isMobilePane={isMobilePane} hideActions={hideActions} onClose={() => clearSelectedMarkerInMap()} />;
    $[60] = clearSelectedMarkerInMap;
    $[61] = description;
    $[62] = editingLogic.editingState;
    $[63] = editingLogic.handleCancel;
    $[64] = editingLogic.handleSave;
    $[65] = editingLogic.isCurrentlyEditing;
    $[66] = editingLogic.isEditingNewlyCreatedPin;
    $[67] = editingLogic.isSaveDisabled;
    $[68] = editingLogic.updateColor;
    $[69] = editingLogic.updateDescription;
    $[70] = editingLogic.updateTitle;
    $[71] = gameCoords;
    $[72] = hideActions;
    $[73] = isEditingNewPinWithoutLocation;
    $[74] = isMobilePane;
    $[75] = sharedProps;
    $[76] = subcategory?.title;
    $[77] = title;
    $[78] = t12;
  } else {
    t12 = $[78];
  }
  let t13;
  if ($[79] !== isExploreActionMenu || $[80] !== isMobilePane || $[81] !== locationStyleOverride || $[82] !== t11 || $[83] !== t12) {
    t13 = <LocationInfoContainer locationStyleOverride={locationStyleOverride} edgeToEdgeContent={t11} isMobilePane={isMobilePane} flushContent={isExploreActionMenu}>{t12}</LocationInfoContainer>;
    $[79] = isExploreActionMenu;
    $[80] = isMobilePane;
    $[81] = locationStyleOverride;
    $[82] = t11;
    $[83] = t12;
    $[84] = t13;
  } else {
    t13 = $[84];
  }
  let t14;
  if ($[85] !== t10 || $[86] !== t13 || $[87] !== t8 || $[88] !== t9) {
    t14 = <FlexColumn className={t8} width="full" height={t9} id="location-info">{t10}{t13}</FlexColumn>;
    $[85] = t10;
    $[86] = t13;
    $[87] = t8;
    $[88] = t9;
    $[89] = t14;
  } else {
    t14 = $[89];
  }
  return t14;
};